import {
  Box,
  Center,
  DesignSystemProvider,
  Loader,
  useBreakpointValue,
  useTheme,
} from '@hover/blueprint';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect } from 'react-router';
import { useParams } from 'react-router-dom';

import { FullscreenImage } from 'src/modules/homeownerApp/components/FullscreenImage';
import { Details } from 'src/modules/homeownerApp/components/ProjectShare/Details';
import { Header } from 'src/modules/homeownerApp/components/ProjectShare/Header';
import { Images } from 'src/modules/homeownerApp/components/ProjectShare/Images';
import { Meta } from 'src/modules/homeownerApp/components/ProjectShare/Meta';
import { MobileImages } from 'src/modules/homeownerApp/components/ProjectShare/MobileImages';
import { OriginalImages } from 'src/modules/homeownerApp/components/ProjectShare/OriginalImages';
import {
  useFilteredOutUneditedOriginalImages,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { HomeownerAppProvider } from 'src/modules/homeownerApp/providers';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { useGetProjectShareQuery } from 'src/shared/graphql/generated';
import { useEffectOnMount } from 'src/shared/hooks';
import { Route, Route as Routes, generatePath } from 'src/shared/route';
import { pageViewed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

const ProjectShare = () => {
  const [visibleIndex, setVisibleIndex] = useState<number>(0);
  const { id } = useParams<{ id: string }>();

  const context = useHomeownerAppContext();
  const { trackingData } = context;

  const { data, error, loading } = useGetProjectShareQuery({
    variables: { id },
  });

  useEffect(() => {
    if (error) logError(error);
  }, [error]);

  const { orgBrandLogoUrl, aiDisplayImages, orgId } = data?.projectShare || {};
  const images: AiDisplayImage[] = useFilteredOutUneditedOriginalImages(
    (aiDisplayImages ?? []) as AiDisplayImage[],
  ) as AiDisplayImage[];

  useEffectOnMount(() => {
    pageViewed({
      ...trackingData,
      feature: 'lead-landing-page',
      page_or_screen_name: 'lead-landing-page',
      track_id: 'lead-landing-page-view',
      org_id: orgId ?? undefined,
    });
  });

  const ImageList = useBreakpointValue(
    {
      base: MobileImages,
      tablet: Images,
    },
    { fallback: 'tablet' },
  );

  const firstAiGeneratedImageUrl =
    aiDisplayImages?.[0]?.aiGeneratedImage.generatedImagePath;

  const { breakpoints } = useTheme();

  const newTrackingData = useMemo(
    () => ({
      orgId,
      feature: FeatureTracking.LEAD_LANDING_PAGE,
      page_or_screen_name: FeatureTracking.LEAD_LANDING_PAGE,
    }),
    [orgId],
  );

  const getShouldRedirectShare =
    (!loading && data?.projectShare === null) || // deleted project or not in split treatment
    (!loading && data?.projectShare?.aiDisplayImages?.length === 0); // all associated images deleted

  if (getShouldRedirectShare) {
    return (
      <Redirect
        from={Routes.PROJECT_SHARE.path}
        to={Routes.HOMEOWNER_APP_HOME.path}
      />
    );
  }

  // TODO: handle error case
  if (loading || !aiDisplayImages) {
    return (
      <Center bottom="0" left="0" position="fixed" right="0" top="0">
        <Loader color="neutral.900" size="huge" />
      </Center>
    );
  }

  return (
    <HomeownerAppProvider
      displayIntercom={false}
      trackingDataOverrides={newTrackingData}
    >
      <DesignSystemProvider attach>
        <Box
          alignSelf="center"
          flexDirection="column"
          margin="auto"
          maxWidth={breakpoints.width500}
        >
          <Meta imageUrl={firstAiGeneratedImageUrl} />
          <Header orgBrandLogoUrl={orgBrandLogoUrl ?? undefined} />
          <Box
            flexDirection={{ base: 'column', tablet: 'row' }}
            gap={{ base: 700, tablet: 500, width500: 900 }}
            marginTop={{ base: 200, tablet: 500 }}
            marginX={{ base: 500, tablet: 400, width500: 'auto' }}
          >
            <Details />
            {!!images.length && (
              <>
                <OriginalImages
                  aiDisplayImages={images}
                  display={{ base: 'none', tablet: 'inherit' }}
                  visibleIndex={visibleIndex}
                />
                <FullscreenImage
                  images={images}
                  isUnauthenticated
                  path={generatePath(Route.PROJECT_SHARE_FULLSCREEN.path, {
                    id,
                    aiDisplayImageId: ':aiDisplayImageId',
                  })}
                  returnPath={generatePath(Route.PROJECT_SHARE.path, { id })}
                >
                  <ImageList
                    aiDisplayImages={images}
                    orgId={orgId}
                    setVisibleIndex={setVisibleIndex}
                    visibleIndex={visibleIndex}
                  />
                </FullscreenImage>
              </>
            )}
          </Box>
        </Box>
      </DesignSystemProvider>
    </HomeownerAppProvider>
  );
};

export { ProjectShare };
